import { useSideEffectOnMount, useWatch } from "@web-monorepo/hooks";
import { useState } from "react";
import { useLocation } from "react-router";
import Zendesk, { ZendeskAPI } from "react-zendesk";
import { getEntityType, type SessionData } from "#/src/hooks/api/auth/session";
import useHelperWidgetAPI, {
  DECAGON_LOADER_SRC,
  hideDecagonWidget,
  getDecagonLoaderScript,
  type WidgetUserData,
} from "#/src/hooks/useHelperWidgetAPI";
import { useSessionQuery } from "#/src/hooks/useSession";
import { logTutorEvent } from "#/src/utils/log";
import { shouldHideWidget } from "./helperWidgetVisibility";
// eslint-disable-next-line @web-monorepo/no-css-file-imports
import "./helper-widget-overrides.css";

const zendeskSettings = {
  contactForm: {
    selectTicketForm: {
      "*": "I need help as a ...",
    },
  },
};

type ZendesScriptProps = {
  setIsLoaded: (_isLoaded: boolean) => void;
};

const ZendeskScript = ({ setIsLoaded }: ZendesScriptProps) => {
  // Config.zendeskWebWidgetKey is `string | null` — skip rendering rather than
  // passing an empty key to Zendesk (which would silently init with a blank
  // widget). The `null` case corresponds to envs without Zendesk configured.
  if (!Config.zendeskWebWidgetKey) return null;
  return (
    <Zendesk
      zendeskKey={Config.zendeskWebWidgetKey}
      {...zendeskSettings}
      onLoaded={() => {
        ZendeskAPI("webWidget", "hide");
        setIsLoaded(true);
      }}
    />
  );
};

type DecagonScriptProps = {
  setIsLoaded: (_isLoaded: boolean) => void;
};

const DecagonScript = ({ setIsLoaded }: DecagonScriptProps) => {
  const HelperWidgetAPI = useHelperWidgetAPI();

  useSideEffectOnMount(() => {
    const onLoad = () => {
      hideDecagonWidget();
      setIsLoaded(true);
      HelperWidgetAPI.setDecagonEventListener();
    };
    // This component unmounts on logout and remounts on the next login; the loader must only be
    // injected once per page load. A remount takes over a loader that's still loading so the
    // previous (unmounted) instance's handler doesn't run.
    const existingScript = getDecagonLoaderScript();
    if (existingScript?.dataset.loaded) {
      onLoad();
      return;
    }
    const script = existingScript ?? document.createElement("script");
    script.onload = () => {
      script.dataset.loaded = "true";
      onLoad();
    };
    if (!existingScript) {
      script.src = DECAGON_LOADER_SRC;
      document.body.appendChild(script);
    }
  });
  return null;
};

const getWidgetUserData = (sessionData: SessionData): WidgetUserData => ({
  userId: sessionData.user_details.uid,
  email: sessionData.user_details.email || "",
  userType: getEntityType(sessionData.roles),
  fullname: `${sessionData.user_details.first_name || ""} ${sessionData.user_details.last_name || ""}`,
  caller: "helperWidgetWatcher",
});

const HelperWidget = () => {
  // Without a Zendesk key (local dev / Playwright), react-zendesk would render a
  // <script src=".../snippet.js?key=null"> that throws a page-level TypeError.
  // Skip rendering <ZendeskScript> entirely and treat Zendesk as "loaded" so the
  // rest of the HelperWidget lifecycle proceeds.
  const [isZendeskLoaded, setIsZendeskLoaded] = useState(!Config.zendeskWebWidgetKey);
  // Not reset on logout: the loader and `window.duet` live for the whole page, so once loaded,
  // Decagon stays usable for the next login.
  const [isDecagonLoaded, setIsDecagonLoaded] = useState(false);
  const helperWidgetAPI = useHelperWidgetAPI();
  const { pathname } = useLocation();
  const { sessionData, isFetching } = useSessionQuery();

  // Only load Decagon for users who get it. Loading it for everyone let logged-out visitors open the
  // default launcher and start chats with no ClassDojo metadata.
  const shouldLoadDecagon = helperWidgetAPI.isDecagonWidget;

  useWatch(
    [isZendeskLoaded, isDecagonLoaded, shouldLoadDecagon, pathname, helperWidgetAPI, sessionData, isFetching],
    () => {
      const isReady = isZendeskLoaded && (isDecagonLoaded || !shouldLoadDecagon) && !isFetching;
      if (!isReady) return;

      const isHiddenRoute = shouldHideWidget(pathname);

      // Decagon users get their data on every route, including ones that hide the launcher:
      // openWidget() can still open Decagon there, and it must not start a conversation without metadata.
      if (sessionData && (shouldLoadDecagon || !isHiddenRoute)) {
        helperWidgetAPI.setWidgetUserData(getWidgetUserData(sessionData));
      }

      if (isHiddenRoute) return;

      if (sessionData && shouldLoadDecagon) {
        logTutorEvent({
          eventName:
            getEntityType(sessionData.roles) === "parent"
              ? "parent.marketplace.decagon_widget_shown"
              : "tutor.marketplace.decagon_widget_shown",
          metadata: { email: sessionData.user_details.email },
        });
      }

      helperWidgetAPI.showWidget();
    },
  );

  return (
    <>
      {shouldLoadDecagon && <DecagonScript setIsLoaded={setIsDecagonLoaded} />}
      {Config.zendeskWebWidgetKey && <ZendeskScript setIsLoaded={setIsZendeskLoaded} />}
    </>
  );
};

interface HelperProviderProps {
  children: React.ReactNode;
}

export default function HelperWidgetProvider({ children }: HelperProviderProps) {
  return (
    <>
      <HelperWidget />
      {children}
    </>
  );
}
